import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Plus, Search, MapPin, User, Calendar, Camera, MessageSquare, Clock, Upload, GripVertical, Keyboard, X, MoreVertical, ChevronRight, HardHat, Ruler, Building, Trash2, AlertTriangle, CheckCircle, XCircle } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import {
  DndContext,
  DragOverlay,
  closestCorners,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  useDroppable,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { PermissionGate, usePermissions } from '../../context/PermissionContext';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const PROJECT_STATUSES = [
  { id: 'new', name: 'New', color: 'bg-neutral-100 text-neutral-700' },
  { id: 'measuring', name: 'Measuring', color: 'bg-blue-100 text-blue-700' },
  { id: 'quote_sent', name: 'Quote Sent', color: 'bg-purple-100 text-purple-700' },
  { id: 'approved', name: 'Approved', color: 'bg-green-100 text-green-700' },
  { id: 'in_production', name: 'In Production', color: 'bg-amber-100 text-amber-700' },
  { id: 'ready_to_install', name: 'Ready to Install', color: 'bg-cyan-100 text-cyan-700' },
  { id: 'scheduled', name: 'Scheduled', color: 'bg-indigo-100 text-indigo-700' },
  { id: 'completed', name: 'Completed', color: 'bg-emerald-100 text-emerald-700' },
];

const PHOTO_STAGES = [
  { id: 'measuring', name: 'Measurement Photos', icon: '📐' },
  { id: 'installation', name: 'Installation Photos', icon: '🔧' },
  { id: 'completion', name: 'Completion Photos', icon: '✅' },
];

// Draggable Project Card Component
const ProjectCard = ({ project, onClick, isDragging, onStatusChange, onQuickAction, userRole }) => {
  const [showActions, setShowActions] = useState(false);
  const actionsRef = useRef(null);
  
  const isInstaller = userRole === 'installer';

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (actionsRef.current && !actionsRef.current.contains(e.target)) {
        setShowActions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleActionClick = (e, action) => {
    e.stopPropagation();
    setShowActions(false);
    if (action.type === 'status') {
      onStatusChange?.(project.id, action.value);
    } else if (action.type === 'action') {
      onQuickAction?.(action.value, project);
    }
  };

  const currentStatusIndex = PROJECT_STATUSES.findIndex(s => s.id === project.status);
  const nextStatus = PROJECT_STATUSES[currentStatusIndex + 1];
  const prevStatus = PROJECT_STATUSES[currentStatusIndex - 1];

  return (
    <div
      onClick={onClick}
      className={`bg-white border border-neutral-200 rounded-lg p-3 cursor-pointer hover:shadow-md transition-all relative group ${
        isDragging ? 'shadow-lg ring-2 ring-amber-400 opacity-90' : ''
      } ${project.measurement_project_id ? 'border-l-4 border-l-amber-400' : ''}`}
    >
      <div className="flex items-start justify-between mb-2">
        <div className="flex items-center gap-2">
          <span className="font-mono text-xs text-amber-600 font-medium">
            {project.project_number}
          </span>
          {project.measurement_project_id && (
            <span className="px-1.5 py-0.5 bg-amber-100 text-amber-700 text-[10px] rounded font-medium">
              MEASURE
            </span>
          )}
        </div>
        <div className="flex items-center gap-1">
          {project.photos?.length > 0 && (
            <Camera className="w-3 h-3 text-neutral-400" />
          )}
          
          {/* Quick Actions Button */}
          <div ref={actionsRef} className="relative">
            <button
              onClick={(e) => {
                e.stopPropagation();
                setShowActions(!showActions);
              }}
              className="p-1 hover:bg-neutral-100 rounded opacity-0 group-hover:opacity-100 transition-opacity"
            >
              <MoreVertical className="w-3.5 h-3.5 text-neutral-500" />
            </button>
            
            {/* Dropdown Menu */}
            {showActions && (
              <div className="absolute right-0 top-6 z-50 bg-white border border-neutral-200 rounded-lg shadow-lg py-1 min-w-[180px]">
                {/* Quick Move */}
                {nextStatus && (
                  <button
                    onClick={(e) => handleActionClick(e, { type: 'status', value: nextStatus.id })}
                    className="w-full px-3 py-2 text-left text-sm hover:bg-neutral-50 flex items-center gap-2"
                  >
                    <ChevronRight className="w-4 h-4 text-green-600" />
                    <span>Move to <strong>{nextStatus.name}</strong></span>
                  </button>
                )}
                {prevStatus && (
                  <button
                    onClick={(e) => handleActionClick(e, { type: 'status', value: prevStatus.id })}
                    className="w-full px-3 py-2 text-left text-sm hover:bg-neutral-50 flex items-center gap-2"
                  >
                    <ChevronRight className="w-4 h-4 rotate-180 text-amber-600" />
                    <span>Back to <strong>{prevStatus.name}</strong></span>
                  </button>
                )}
                
                <div className="border-t border-neutral-100 my-1" />
                
                {/* Status Submenu */}
                <div className="px-3 py-1.5 text-xs text-neutral-500 font-medium">Change Status</div>
                <div className="max-h-48 overflow-y-auto">
                  {PROJECT_STATUSES.map((status, i) => (
                    <button
                      key={status.id}
                      onClick={(e) => handleActionClick(e, { type: 'status', value: status.id })}
                      disabled={status.id === project.status}
                      className={`w-full px-3 py-1.5 text-left text-sm hover:bg-neutral-50 flex items-center gap-2 ${
                        status.id === project.status ? 'bg-neutral-50 text-neutral-400' : ''
                      }`}
                    >
                      <span className={`w-5 h-5 rounded flex items-center justify-center text-xs font-medium ${status.color}`}>
                        {i + 1}
                      </span>
                      <span>{status.name}</span>
                      {status.id === project.status && <span className="ml-auto text-xs text-neutral-400">Current</span>}
                    </button>
                  ))}
                </div>
                
                <div className="border-t border-neutral-100 my-1" />
                
                {/* Quick Actions */}
                <button
                  onClick={(e) => handleActionClick(e, { type: 'action', value: 'view' })}
                  className="w-full px-3 py-2 text-left text-sm hover:bg-neutral-50"
                >
                  View Details
                </button>
              </div>
            )}
          </div>
          
          <GripVertical className="w-3 h-3 text-neutral-300" />
        </div>
      </div>
      
      {/* Customer/Client Name */}
      <h4 className="font-medium text-sm mb-1">{project.customer_name}</h4>
      
      {/* Jobsite Address - always show for trade/sales/installer */}
      {project.site_address && (
        <p className="text-xs text-neutral-500 flex items-center gap-1 mb-1">
          <MapPin className="w-3 h-3" />
          {project.site_address}
          {project.site_city && `, ${project.site_city}`}
          {project.site_state && `, ${project.site_state}`}
        </p>
      )}
      
      {/* Creator info for visibility */}
      {project.created_by_display && (
        <p className="text-xs text-blue-500 flex items-center gap-1 mb-2">
          <Building className="w-3 h-3" />
          {project.created_by_display}
          {project.created_by_role && project.created_by_role !== 'admin' && (
            <span className="text-neutral-400">({project.created_by_role})</span>
          )}
        </p>
      )}
      
      <div className="flex items-center justify-between text-xs text-neutral-400">
        {/* Hide pricing for installers */}
        {!isInstaller && <span>{formatCurrency(project.total)}</span>}
        {isInstaller && <span className="text-neutral-300">—</span>}
        {project.installation_date && (
          <span className="flex items-center gap-1">
            <Calendar className="w-3 h-3" />
            {formatDate(project.installation_date)}
          </span>
        )}
      </div>
      {(project.assigned_installer_name || project.assigned_to) && (
        <div className="mt-2 pt-2 border-t border-neutral-100 flex flex-wrap gap-2">
          {project.assigned_installer_name && (
            <span className="text-xs text-neutral-500 flex items-center gap-1">
              <HardHat className="w-3 h-3" />
              {project.assigned_installer_name}
            </span>
          )}
          {project.assigned_to && !project.assigned_installer_name && (
            <span className="text-xs text-blue-500 flex items-center gap-1">
              <User className="w-3 h-3" />
              Assigned
            </span>
          )}
        </div>
      )}
    </div>
  );
};

// Sortable Project Card wrapper
const SortableProjectCard = ({ project, onOpenDetail, onStatusChange, onQuickAction, userRole }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: project.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
      <ProjectCard 
        project={project} 
        onClick={() => !isDragging && onOpenDetail(project)}
        isDragging={isDragging}
        onStatusChange={onStatusChange}
        onQuickAction={onQuickAction}
        userRole={userRole}
      />
    </div>
  );
};

// Droppable Column Component
const KanbanColumn = ({ status, projects, onOpenDetail, onStatusChange, onQuickAction, userRole }) => {
  const { setNodeRef, isOver } = useDroppable({
    id: status.id,
  });

  return (
    <div className="flex-shrink-0 w-72">
      {/* Column Header */}
      <div className="flex items-center justify-between mb-3 px-2">
        <div className="flex items-center gap-2">
          <span className={`px-2 py-1 rounded text-xs font-medium ${status.color}`}>
            {status.name}
          </span>
          <span className="text-sm text-neutral-400">
            {projects.length}
          </span>
        </div>
      </div>
      
      {/* Column Cards */}
      <div 
        ref={setNodeRef}
        className={`space-y-3 min-h-[200px] rounded-lg p-2 transition-colors ${
          isOver ? 'bg-amber-50 ring-2 ring-amber-200' : 'bg-neutral-50'
        }`}
      >
        <SortableContext items={projects.map(p => p.id)} strategy={verticalListSortingStrategy}>
          {projects.map(project => (
            <SortableProjectCard 
              key={project.id} 
              project={project} 
              onOpenDetail={onOpenDetail}
              onStatusChange={onStatusChange}
              onQuickAction={onQuickAction}
              userRole={userRole}
            />
          ))}
        </SortableContext>
        {projects.length === 0 && (
          <p className="text-xs text-neutral-400 text-center py-8">
            {isOver ? 'Drop here' : 'No projects'}
          </p>
        )}
      </div>
    </div>
  );
};

export const ProjectsTab = ({ token, onNavigateToMeasurements, userRole }) => {
  const [projects, setProjects] = useState([]);
  const [installers, setInstallers] = useState([]);
  const [salesReps, setSalesReps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState('kanban');
  const [selectedProject, setSelectedProject] = useState(null);
  const [showProjectModal, setShowProjectModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newNote, setNewNote] = useState('');
  const [activePhotoStage, setActivePhotoStage] = useState('measuring');
  const [activeId, setActiveId] = useState(null);
  const [activeProject, setActiveProject] = useState(null);
  const [selectedCardIndex, setSelectedCardIndex] = useState(-1);
  const [showShortcuts, setShowShortcuts] = useState(false);
  const searchInputRef = useRef(null);
  
  // Deletion state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showRequestDeletionModal, setShowRequestDeletionModal] = useState(false);
  const [projectToDelete, setProjectToDelete] = useState(null);
  const [deletionReason, setDeletionReason] = useState('');
  const [deletionRequests, setDeletionRequests] = useState([]);
  const [showDeletionRequestsModal, setShowDeletionRequestsModal] = useState(false);
  const [processingRequest, setProcessingRequest] = useState(null);
  const [adminReason, setAdminReason] = useState('');
  
  // Check if user is admin from permissions context
  const { isAdmin } = usePermissions();

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Define filteredProjects early so it can be used in keyboard handler
  const filteredProjects = projects.filter(p =>
    p.project_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.customer_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.site_address?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getProjectsByStatus = (status) => {
    return filteredProjects.filter(p => p.status === status);
  };

  const getStatusInfo = (statusId) => {
    return PROJECT_STATUSES.find(s => s.id === statusId) || PROJECT_STATUSES[0];
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount || 0);
  };

  // Keyboard shortcuts handler
  const handleKeyDown = useCallback((e) => {
    // Don't trigger shortcuts when typing in input fields
    if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA' || e.target.tagName === 'SELECT') {
      if (e.key === 'Escape') {
        e.target.blur();
      }
      return;
    }

    // Close any open modal with Escape
    if (e.key === 'Escape') {
      if (showProjectModal) {
        setShowProjectModal(false);
        return;
      }
      if (showCreateModal) {
        setShowCreateModal(false);
        return;
      }
      if (showShortcuts) {
        setShowShortcuts(false);
        return;
      }
      setSelectedCardIndex(-1);
      return;
    }

    // Don't process shortcuts if a modal is open
    if (showProjectModal || showCreateModal) return;

    switch (e.key.toLowerCase()) {
      case 'n':
        // New project
        e.preventDefault();
        setShowCreateModal(true);
        break;
      case '/':
      case 's':
        // Focus search
        e.preventDefault();
        searchInputRef.current?.focus();
        break;
      case 'b':
        // Switch to board view
        e.preventDefault();
        setViewMode('kanban');
        toast.success('Switched to Board view');
        break;
      case 'l':
        // Switch to list view
        e.preventDefault();
        setViewMode('list');
        toast.success('Switched to List view');
        break;
      case 'r':
        // Refresh projects
        e.preventDefault();
        fetchProjects();
        toast.success('Refreshing projects...');
        break;
      case '?':
        // Show shortcuts help
        e.preventDefault();
        setShowShortcuts(true);
        break;
      case 'arrowdown':
      case 'j':
        // Navigate down
        e.preventDefault();
        setSelectedCardIndex(prev => Math.min(prev + 1, filteredProjects.length - 1));
        break;
      case 'arrowup':
      case 'k':
        // Navigate up
        e.preventDefault();
        setSelectedCardIndex(prev => Math.max(prev - 1, 0));
        break;
      case 'enter':
        // Open selected project
        if (selectedCardIndex >= 0 && selectedCardIndex < filteredProjects.length) {
          e.preventDefault();
          openProjectDetail(filteredProjects[selectedCardIndex]);
        }
        break;
      default:
        // Check for number keys 1-8 for status
        const num = parseInt(e.key);
        if (num >= 1 && num <= 8 && selectedCardIndex >= 0) {
          e.preventDefault();
          const project = filteredProjects[selectedCardIndex];
          const newStatus = PROJECT_STATUSES[num - 1]?.id;
          if (project && newStatus && project.status !== newStatus) {
            updateProjectStatus(project.id, newStatus);
          }
        }
        break;
    }
  }, [showProjectModal, showCreateModal, showShortcuts, selectedCardIndex, filteredProjects]);

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  useEffect(() => {
    fetchProjects();
    fetchInstallers();
    fetchSalesReps();
    if (isAdmin) {
      fetchDeletionRequests();
    }
  }, [isAdmin]);

  const fetchProjects = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/admin/projects`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProjects(response.data);
    } catch (error) {
      console.error('Failed to fetch projects:', error);
      toast.error('Failed to load projects');
    } finally {
      setLoading(false);
    }
  };

  const fetchInstallers = async () => {
    try {
      const response = await axios.get(`${API_URL}/admin/installers`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setInstallers(response.data);
    } catch (error) {
      console.error('Failed to fetch installers:', error);
    }
  };

  const fetchSalesReps = async () => {
    try {
      const response = await axios.get(`${API_URL}/admin/company-users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const salesRoles = ['role_sales_rep', 'role_sales_manager', 'role_admin', 'role_super_admin'];
      const reps = response.data.filter(user => 
        user.is_active !== false && 
        (salesRoles.includes(user.role_id) || user.is_admin)
      );
      setSalesReps(reps);
    } catch (error) {
      console.error('Failed to fetch sales reps:', error);
    }
  };

  const fetchDeletionRequests = async () => {
    try {
      const response = await axios.get(`${API_URL}/admin/deletion-requests?status_filter=pending`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDeletionRequests(response.data);
    } catch (error) {
      console.error('Failed to fetch deletion requests:', error);
    }
  };

  // Delete project directly (admin only)
  const handleDeleteProject = async () => {
    if (!projectToDelete) return;
    
    try {
      await axios.delete(`${API_URL}/admin/projects/${projectToDelete.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Project deleted successfully');
      setShowDeleteModal(false);
      setProjectToDelete(null);
      fetchProjects();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete project');
    }
  };

  // Request deletion (non-admin users)
  const handleRequestDeletion = async () => {
    if (!projectToDelete || !deletionReason.trim()) {
      toast.error('Please provide a reason for deletion');
      return;
    }
    
    try {
      await axios.post(`${API_URL}/admin/projects/${projectToDelete.id}/request-deletion`, 
        { reason: deletionReason },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Deletion request submitted for admin approval');
      setShowRequestDeletionModal(false);
      setProjectToDelete(null);
      setDeletionReason('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit deletion request');
    }
  };

  // Approve deletion request (admin only)
  const handleApproveDeletionRequest = async (requestId) => {
    if (!adminReason.trim()) {
      toast.error('Please provide a reason for approval');
      return;
    }
    
    try {
      await axios.put(`${API_URL}/admin/deletion-requests/${requestId}/approve`, 
        { admin_reason: adminReason },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Deletion request approved and project deleted');
      setProcessingRequest(null);
      setAdminReason('');
      fetchDeletionRequests();
      fetchProjects();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve deletion request');
    }
  };

  // Reject deletion request (admin only)
  const handleRejectDeletionRequest = async (requestId) => {
    if (!adminReason.trim()) {
      toast.error('Please provide a reason for rejection');
      return;
    }
    
    try {
      await axios.put(`${API_URL}/admin/deletion-requests/${requestId}/reject`, 
        { admin_reason: adminReason },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Deletion request rejected');
      setProcessingRequest(null);
      setAdminReason('');
      fetchDeletionRequests();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reject deletion request');
    }
  };

  // Open delete/request deletion modal based on user role
  const openDeleteDialog = (project) => {
    setProjectToDelete(project);
    if (isAdmin) {
      setShowDeleteModal(true);
    } else {
      setShowRequestDeletionModal(true);
    }
  };

  const updateProjectStatus = async (projectId, newStatus) => {
    try {
      await axios.put(`${API_URL}/admin/projects/${projectId}`, 
        { status: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchProjects();
      toast.success('Project moved to ' + PROJECT_STATUSES.find(s => s.id === newStatus)?.name);
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleDragStart = (event) => {
    const { active } = event;
    setActiveId(active.id);
    setActiveProject(projects.find(p => p.id === active.id));
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    setActiveId(null);
    setActiveProject(null);

    if (!over) return;

    const projectId = active.id;
    const project = projects.find(p => p.id === projectId);
    
    // Check if dropped on a column (status)
    const newStatus = over.id;
    
    // Only update if status is different
    if (PROJECT_STATUSES.some(s => s.id === newStatus) && project?.status !== newStatus) {
      // Optimistically update the UI
      setProjects(prev => prev.map(p => 
        p.id === projectId ? { ...p, status: newStatus } : p
      ));
      // Then update the server
      updateProjectStatus(projectId, newStatus);
    }
  };

  const handleDragOver = (event) => {
    const { active, over } = event;
    if (!over) return;

    const activeProject = projects.find(p => p.id === active.id);
    const overId = over.id;

    // If hovering over a column
    if (PROJECT_STATUSES.some(s => s.id === overId)) {
      if (activeProject?.status !== overId) {
        setProjects(prev => prev.map(p => 
          p.id === active.id ? { ...p, status: overId } : p
        ));
      }
    }
  };

  const addNote = async () => {
    if (!newNote.trim() || !selectedProject) return;
    try {
      await axios.post(`${API_URL}/admin/projects/${selectedProject.id}/notes`,
        { text: newNote },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setNewNote('');
      const response = await axios.get(`${API_URL}/admin/projects/${selectedProject.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedProject(response.data);
      toast.success('Note added');
    } catch (error) {
      toast.error('Failed to add note');
    }
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file || !selectedProject) return;

    // Create FormData for file upload
    const formData = new FormData();
    formData.append('file', file);
    formData.append('stage', activePhotoStage);
    formData.append('caption', file.name);
    
    try {
      toast.loading('Uploading photo...');
      await axios.post(`${API_URL}/admin/projects/${selectedProject.id}/upload-photo`,
        formData,
        { 
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          } 
        }
      );
      toast.dismiss();
      const response = await axios.get(`${API_URL}/admin/projects/${selectedProject.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedProject(response.data);
      toast.success('Photo uploaded');
    } catch (error) {
      toast.dismiss();
      toast.error('Failed to upload photo');
    }
    
    // Reset the input
    e.target.value = '';
  };

  const openProjectDetail = async (project) => {
    try {
      const response = await axios.get(`${API_URL}/admin/projects/${project.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedProject(response.data);
      setShowProjectModal(true);
    } catch (error) {
      toast.error('Failed to load project details');
    }
  };

  // Reset selected card when search changes
  useEffect(() => {
    setSelectedCardIndex(-1);
  }, [searchTerm]);

  return (
    <div data-testid="projects-tab">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-semibold">Projects</h2>
          <p className="text-neutral-500 text-sm">Track jobs from quote to completion - drag cards to update status</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowShortcuts(true)}
            className="p-2 hover:bg-neutral-100 rounded-lg transition-colors"
            title="Keyboard shortcuts (?)"
          >
            <Keyboard className="w-4 h-4 text-neutral-500" />
          </button>
          <div className="flex bg-neutral-100 rounded-lg p-1">
            <button
              onClick={() => setViewMode('kanban')}
              className={`px-3 py-1 text-sm rounded ${viewMode === 'kanban' ? 'bg-white shadow' : ''}`}
            >
              Board
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`px-3 py-1 text-sm rounded ${viewMode === 'list' ? 'bg-white shadow' : ''}`}
            >
              List
            </button>
          </div>
          <div className="flex items-center gap-2">
            {/* Deletion Requests Badge for Admins */}
            {isAdmin && deletionRequests.length > 0 && (
              <Button 
                variant="outline" 
                className="border-red-200 text-red-600 hover:bg-red-50 relative"
                onClick={() => setShowDeletionRequestsModal(true)}
                data-testid="deletion-requests-btn"
              >
                <AlertTriangle className="w-4 h-4 mr-1" />
                Deletion Requests
                <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
                  {deletionRequests.length}
                </span>
              </Button>
            )}
            <PermissionGate module="projects" level="full">
            <Button 
              onClick={onNavigateToMeasurements} 
              variant="outline"
              className="border-amber-300 text-amber-700 hover:bg-amber-50"
              data-testid="new-measurement-btn"
            >
              <Ruler className="w-4 h-4 mr-2" /> New Measurement
            </Button>
            </PermissionGate>
            <PermissionGate module="projects" level="full">
            <Button onClick={() => setShowCreateModal(true)} className="btn-primary">
              <Plus className="w-4 h-4 mr-2" /> New Project
            </Button>
            </PermissionGate>
          </div>
        </div>
      </div>

      {/* Search */}
      <div className="flex gap-4 mb-6">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
          <Input
            ref={searchInputRef}
            placeholder="Search projects... (press / or S)"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
      </div>

      {/* Keyboard Shortcuts Help */}
      <Dialog open={showShortcuts} onOpenChange={setShowShortcuts}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Keyboard className="w-5 h-5" /> Keyboard Shortcuts
            </DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div>
              <h4 className="text-sm font-medium text-neutral-500 mb-2">Navigation</h4>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div className="flex items-center justify-between bg-neutral-50 rounded px-3 py-2">
                  <span>Search</span>
                  <kbd className="px-2 py-0.5 bg-white border rounded text-xs font-mono">/</kbd>
                </div>
                <div className="flex items-center justify-between bg-neutral-50 rounded px-3 py-2">
                  <span>Navigate up</span>
                  <kbd className="px-2 py-0.5 bg-white border rounded text-xs font-mono">↑ / K</kbd>
                </div>
                <div className="flex items-center justify-between bg-neutral-50 rounded px-3 py-2">
                  <span>Navigate down</span>
                  <kbd className="px-2 py-0.5 bg-white border rounded text-xs font-mono">↓ / J</kbd>
                </div>
                <div className="flex items-center justify-between bg-neutral-50 rounded px-3 py-2">
                  <span>Open project</span>
                  <kbd className="px-2 py-0.5 bg-white border rounded text-xs font-mono">Enter</kbd>
                </div>
              </div>
            </div>
            
            <div>
              <h4 className="text-sm font-medium text-neutral-500 mb-2">Actions</h4>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div className="flex items-center justify-between bg-neutral-50 rounded px-3 py-2">
                  <span>New project</span>
                  <kbd className="px-2 py-0.5 bg-white border rounded text-xs font-mono">N</kbd>
                </div>
                <div className="flex items-center justify-between bg-neutral-50 rounded px-3 py-2">
                  <span>Refresh</span>
                  <kbd className="px-2 py-0.5 bg-white border rounded text-xs font-mono">R</kbd>
                </div>
                <div className="flex items-center justify-between bg-neutral-50 rounded px-3 py-2">
                  <span>Board view</span>
                  <kbd className="px-2 py-0.5 bg-white border rounded text-xs font-mono">B</kbd>
                </div>
                <div className="flex items-center justify-between bg-neutral-50 rounded px-3 py-2">
                  <span>List view</span>
                  <kbd className="px-2 py-0.5 bg-white border rounded text-xs font-mono">L</kbd>
                </div>
              </div>
            </div>

            <div>
              <h4 className="text-sm font-medium text-neutral-500 mb-2">Quick Status Change (select project first)</h4>
              <div className="flex flex-wrap gap-1.5 text-xs">
                {PROJECT_STATUSES.map((status, i) => (
                  <div key={status.id} className={`flex items-center gap-1 px-2 py-1 rounded ${status.color}`}>
                    <kbd className="px-1.5 py-0.5 bg-white/50 rounded font-mono">{i + 1}</kbd>
                    <span>{status.name}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="pt-2 border-t">
              <div className="flex items-center justify-between bg-neutral-50 rounded px-3 py-2 text-sm">
                <span>Close / Cancel</span>
                <kbd className="px-2 py-0.5 bg-white border rounded text-xs font-mono">Esc</kbd>
              </div>
            </div>
          </div>
          <div className="flex justify-end">
            <Button variant="outline" onClick={() => setShowShortcuts(false)}>Close</Button>
          </div>
        </DialogContent>
      </Dialog>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="w-8 h-8 border-2 border-neutral-300 border-t-neutral-900 rounded-full animate-spin" />
        </div>
      ) : viewMode === 'kanban' ? (
        /* Kanban Board View with Drag and Drop */
        <DndContext
          sensors={sensors}
          collisionDetection={closestCorners}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
          onDragOver={handleDragOver}
        >
          <div className="flex gap-4 overflow-x-auto pb-4">
            {PROJECT_STATUSES.map(status => (
              <KanbanColumn
                key={status.id}
                status={status}
                projects={getProjectsByStatus(status.id)}
                onOpenDetail={openProjectDetail}
                onStatusChange={updateProjectStatus}
                onQuickAction={(action, project) => {
                  if (action === 'view') openProjectDetail(project);
                }}
                userRole={userRole}
              />
            ))}
          </div>
          
          <DragOverlay>
            {activeProject ? (
              <div className="w-72">
                <ProjectCard project={activeProject} isDragging userRole={userRole} />
              </div>
            ) : null}
          </DragOverlay>
        </DndContext>
      ) : (
        /* List View */
        <div className="border border-neutral-200 rounded-lg overflow-hidden">
          <table className="w-full">
            <thead className="bg-neutral-50 border-b">
              <tr>
                <th className="text-left px-4 py-3 text-sm font-medium text-neutral-600">Project #</th>
                <th className="text-left px-4 py-3 text-sm font-medium text-neutral-600">Customer</th>
                <th className="text-left px-4 py-3 text-sm font-medium text-neutral-600">Location</th>
                <th className="text-left px-4 py-3 text-sm font-medium text-neutral-600">Status</th>
                <th className="text-left px-4 py-3 text-sm font-medium text-neutral-600">Installer</th>
                {userRole !== 'installer' && (
                  <th className="text-right px-4 py-3 text-sm font-medium text-neutral-600">Total</th>
                )}
                <th className="text-left px-4 py-3 text-sm font-medium text-neutral-600">Install Date</th>
                <th className="text-left px-4 py-3 text-sm font-medium text-neutral-600">Created By</th>
              </tr>
            </thead>
            <tbody>
              {filteredProjects.map(project => {
                const statusInfo = getStatusInfo(project.status);
                return (
                  <tr 
                    key={project.id} 
                    className="border-b last:border-0 hover:bg-neutral-50 cursor-pointer"
                    onClick={() => openProjectDetail(project)}
                  >
                    <td className="px-4 py-3 font-mono text-sm font-medium text-amber-600">
                      <div className="flex items-center gap-2">
                        {project.project_number}
                        {project.measurement_project_id && (
                          <span className="px-1.5 py-0.5 bg-amber-100 text-amber-700 text-[10px] rounded font-medium">
                            M
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <p className="font-medium text-sm">{project.customer_name}</p>
                      <p className="text-xs text-neutral-400">{project.customer_email}</p>
                    </td>
                    <td className="px-4 py-3 text-sm text-neutral-600">
                      {project.site_address && <p className="text-xs">{project.site_address}</p>}
                      {project.site_city}, {project.site_state}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 rounded text-xs font-medium ${statusInfo.color}`}>
                        {statusInfo.name}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-neutral-600">
                      {project.assigned_installer_name || '-'}
                    </td>
                    {userRole !== 'installer' && (
                      <td className="px-4 py-3 text-right font-medium">
                        {formatCurrency(project.total)}
                      </td>
                    )}
                    <td className="px-4 py-3 text-sm text-neutral-600">
                      {formatDate(project.installation_date)}
                    </td>
                    <td className="px-4 py-3 text-sm text-neutral-500">
                      {project.created_by_display || project.created_by_name || '-'}
                      {project.created_by_role && project.created_by_role !== 'admin' && (
                        <span className="text-xs text-neutral-400 ml-1">({project.created_by_role})</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Project Detail Modal */}
      <Dialog open={showProjectModal} onOpenChange={setShowProjectModal}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col p-0">
          {selectedProject && (
            <>
              {/* Header */}
              <div className="bg-neutral-900 text-white px-6 py-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-xl font-semibold">{selectedProject.project_number}</h2>
                    <p className="text-neutral-400 text-sm">{selectedProject.customer_name}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <select
                      value={selectedProject.status}
                      onChange={(e) => {
                        updateProjectStatus(selectedProject.id, e.target.value);
                        setSelectedProject(prev => ({ ...prev, status: e.target.value }));
                      }}
                      className="bg-neutral-800 text-white text-sm px-3 py-1.5 rounded border border-neutral-700"
                    >
                      {PROJECT_STATUSES.map(s => (
                        <option key={s.id} value={s.id}>{s.name}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              {/* Content */}
              <div className="flex-1 overflow-y-auto p-6">
                <div className="grid grid-cols-3 gap-6">
                  {/* Left Column - Info */}
                  <div className="col-span-2 space-y-6">
                    {/* Customer & Site Info */}
                    <div className="grid grid-cols-2 gap-4">
                      <div className="bg-neutral-50 rounded-lg p-4">
                        <h4 className="text-sm font-medium text-neutral-500 mb-2">Customer</h4>
                        <p className="font-medium">{selectedProject.customer_name}</p>
                        <p className="text-sm text-neutral-600">{selectedProject.customer_email}</p>
                        <p className="text-sm text-neutral-600">{selectedProject.customer_phone}</p>
                      </div>
                      <div className="bg-neutral-50 rounded-lg p-4">
                        <h4 className="text-sm font-medium text-neutral-500 mb-2">Site Location</h4>
                        <p className="text-sm">{selectedProject.site_address}</p>
                        <p className="text-sm">{selectedProject.site_city}, {selectedProject.site_state} {selectedProject.site_zip}</p>
                        {selectedProject.access_notes && (
                          <p className="text-xs text-amber-600 mt-2">📝 {selectedProject.access_notes}</p>
                        )}
                      </div>
                    </div>

                    {/* Photos by Stage */}
                    <div className="bg-white border border-neutral-200 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-4">
                        <h4 className="font-medium">Project Photos</h4>
                        <div className="flex gap-1">
                          {PHOTO_STAGES.map(stage => (
                            <button
                              key={stage.id}
                              onClick={() => setActivePhotoStage(stage.id)}
                              className={`px-3 py-1 text-sm rounded ${activePhotoStage === stage.id ? 'bg-amber-100 text-amber-700' : 'bg-neutral-100'}`}
                            >
                              {stage.icon} {stage.name.split(' ')[0]}
                            </button>
                          ))}
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-4 gap-3 mb-4">
                        {selectedProject.photos
                          ?.filter(p => p.stage === activePhotoStage)
                          .map(photo => (
                            <div key={photo.id} className="aspect-square bg-neutral-100 rounded-lg overflow-hidden">
                              <img src={photo.url} alt={photo.caption} className="w-full h-full object-cover" />
                            </div>
                          ))}
                      </div>
                      
                      <label className="flex items-center justify-center gap-2 py-3 border-2 border-dashed border-neutral-200 rounded-lg cursor-pointer hover:border-amber-300">
                        <Upload className="w-4 h-4 text-neutral-400" />
                        <span className="text-sm text-neutral-500">Upload {PHOTO_STAGES.find(s => s.id === activePhotoStage)?.name}</span>
                        <input type="file" accept="image/*" className="hidden" onChange={handlePhotoUpload} />
                      </label>
                    </div>

                    {/* Notes */}
                    <div className="bg-white border border-neutral-200 rounded-lg p-4">
                      <h4 className="font-medium mb-3 flex items-center gap-2">
                        <MessageSquare className="w-4 h-4" /> Notes & Activity
                      </h4>
                      
                      <div className="flex gap-2 mb-4">
                        <Input
                          value={newNote}
                          onChange={(e) => setNewNote(e.target.value)}
                          placeholder="Add a note..."
                          className="flex-1"
                        />
                        <Button onClick={addNote} size="sm">Add</Button>
                      </div>
                      
                      <div className="space-y-3 max-h-48 overflow-y-auto">
                        {selectedProject.notes?.slice().reverse().map(note => (
                          <div key={note.id} className="bg-neutral-50 rounded p-3">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="font-medium text-sm">{note.author}</span>
                              <span className="text-xs text-neutral-400">
                                <Clock className="w-3 h-3 inline mr-1" />
                                {formatDate(note.created_at)}
                              </span>
                            </div>
                            <p className="text-sm text-neutral-700">{note.text}</p>
                          </div>
                        ))}
                        {(!selectedProject.notes || selectedProject.notes.length === 0) && (
                          <p className="text-sm text-neutral-400 text-center py-4">No notes yet</p>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Right Column - Summary */}
                  <div className="space-y-4">
                    <div className="bg-neutral-50 rounded-lg p-4">
                      <h4 className="text-sm font-medium text-neutral-500 mb-3">Project Value</h4>
                      <p className="text-2xl font-semibold text-amber-600">{formatCurrency(selectedProject.total)}</p>
                    </div>

                    <div className="bg-neutral-50 rounded-lg p-4">
                      <h4 className="text-sm font-medium text-neutral-500 mb-3">Installer</h4>
                      {selectedProject.assigned_installer_name ? (
                        <p className="font-medium">{selectedProject.assigned_installer_name}</p>
                      ) : (
                        <select
                          className="w-full px-3 py-2 border border-neutral-200 rounded-lg text-sm"
                          onChange={async (e) => {
                            const installer = installers.find(i => i.id === e.target.value);
                            if (installer) {
                              await axios.put(`${API_URL}/admin/projects/${selectedProject.id}`, 
                                { assigned_installer_id: installer.id, assigned_installer_name: installer.name },
                                { headers: { Authorization: `Bearer ${token}` } }
                              );
                              openProjectDetail(selectedProject);
                            }
                          }}
                        >
                          <option value="">Assign installer...</option>
                          {installers.map(i => (
                            <option key={i.id} value={i.id}>{i.name}</option>
                          ))}
                        </select>
                      )}
                    </div>

                    <PermissionGate module="user_management" level="view" fallback={null}>
                    <div className="bg-neutral-50 rounded-lg p-4">
                      <h4 className="text-sm font-medium text-neutral-500 mb-3">Sales Rep</h4>
                      <select
                        className="w-full px-3 py-2 border border-neutral-200 rounded-lg text-sm"
                        value={selectedProject.assigned_to || ''}
                        data-testid="project-assigned-to-select"
                        onChange={async (e) => {
                          const repId = e.target.value || null;
                          try {
                            await axios.put(`${API_URL}/admin/projects/${selectedProject.id}`, 
                              { assigned_to: repId },
                              { headers: { Authorization: `Bearer ${token}` } }
                            );
                            setSelectedProject({...selectedProject, assigned_to: repId});
                            fetchProjects();
                            toast.success('Sales rep updated');
                          } catch (error) {
                            toast.error('Failed to update sales rep');
                          }
                        }}
                      >
                        <option value="">Unassigned</option>
                        {salesReps.map(rep => (
                          <option key={rep.id} value={rep.id}>
                            {rep.name || rep.email}
                          </option>
                        ))}
                      </select>
                    </div>
                    </PermissionGate>

                    <div className="bg-neutral-50 rounded-lg p-4">
                      <h4 className="text-sm font-medium text-neutral-500 mb-3">Schedule</h4>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-neutral-500">Measurement:</span>
                          <span>{formatDate(selectedProject.measurement_date)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-neutral-500">Installation:</span>
                          <span>{formatDate(selectedProject.installation_date)}</span>
                        </div>
                      </div>
                    </div>

                    {selectedProject.quote_number && (
                      <div className="bg-neutral-50 rounded-lg p-4">
                        <h4 className="text-sm font-medium text-neutral-500 mb-3">Linked Documents</h4>
                        <div className="space-y-2 text-sm">
                          {selectedProject.quote_number && (
                            <p className="text-blue-600">Quote: {selectedProject.quote_number}</p>
                          )}
                          {selectedProject.sales_order_id && (
                            <p className="text-purple-600">Sales Order: Linked</p>
                          )}
                          {selectedProject.invoice_id && (
                            <p className="text-green-600">Invoice: Linked</p>
                          )}
                        </div>
                      </div>
                    )}
                    
                    {/* Delete/Request Deletion Button */}
                    <div className="bg-red-50 rounded-lg p-4 border border-red-100">
                      <h4 className="text-sm font-medium text-red-700 mb-2 flex items-center gap-2">
                        <AlertTriangle className="w-4 h-4" /> Danger Zone
                      </h4>
                      <p className="text-xs text-red-600 mb-3">
                        {isAdmin 
                          ? "Permanently delete this project and all associated data."
                          : "Request deletion of this project. An admin will review and approve."}
                      </p>
                      <Button 
                        variant="destructive" 
                        size="sm"
                        onClick={() => {
                          setShowProjectModal(false);
                          openDeleteDialog(selectedProject);
                        }}
                        data-testid="delete-project-btn"
                      >
                        <Trash2 className="w-4 h-4 mr-1" />
                        {isAdmin ? 'Delete Project' : 'Request Deletion'}
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Admin Delete Confirmation Modal */}
      <Dialog open={showDeleteModal} onOpenChange={setShowDeleteModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <Trash2 className="w-5 h-5" /> Delete Project
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-neutral-600 mb-4">
              Are you sure you want to permanently delete this project?
            </p>
            {projectToDelete && (
              <div className="bg-neutral-50 rounded-lg p-3 mb-4">
                <p className="font-medium">{projectToDelete.project_number}</p>
                <p className="text-sm text-neutral-500">{projectToDelete.customer_name}</p>
                <p className="text-sm text-neutral-500">{projectToDelete.site_address}</p>
              </div>
            )}
            <p className="text-xs text-red-600 bg-red-50 p-3 rounded-lg">
              <strong>Warning:</strong> This action cannot be undone. All project data, photos, notes, and history will be permanently removed.
            </p>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => {
              setShowDeleteModal(false);
              setProjectToDelete(null);
            }}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDeleteProject} data-testid="confirm-delete-btn">
              <Trash2 className="w-4 h-4 mr-1" /> Delete Permanently
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Request Deletion Modal (for non-admin users) */}
      <Dialog open={showRequestDeletionModal} onOpenChange={setShowRequestDeletionModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-500" /> Request Project Deletion
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-neutral-600 mb-4">
              Submit a deletion request for admin approval.
            </p>
            {projectToDelete && (
              <div className="bg-neutral-50 rounded-lg p-3 mb-4">
                <p className="font-medium">{projectToDelete.project_number}</p>
                <p className="text-sm text-neutral-500">{projectToDelete.customer_name}</p>
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="deletion-reason">Reason for deletion <span className="text-red-500">*</span></Label>
              <Textarea
                id="deletion-reason"
                placeholder="Please explain why this project should be deleted..."
                value={deletionReason}
                onChange={(e) => setDeletionReason(e.target.value)}
                rows={3}
                data-testid="deletion-reason-input"
              />
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => {
              setShowRequestDeletionModal(false);
              setProjectToDelete(null);
              setDeletionReason('');
            }}>
              Cancel
            </Button>
            <Button 
              onClick={handleRequestDeletion} 
              disabled={!deletionReason.trim()}
              data-testid="submit-deletion-request-btn"
            >
              Submit Request
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Deletion Requests Management Modal (Admin only) */}
      <Dialog open={showDeletionRequestsModal} onOpenChange={setShowDeletionRequestsModal}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-500" /> Pending Deletion Requests
            </DialogTitle>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto py-4">
            {deletionRequests.length === 0 ? (
              <p className="text-center text-neutral-500 py-8">No pending deletion requests</p>
            ) : (
              <div className="space-y-4">
                {deletionRequests.map(request => (
                  <div key={request.id} className="bg-neutral-50 rounded-lg p-4 border">
                    <div className="flex justify-between items-start mb-3">
                      <div>
                        <p className="font-medium">{request.resource_name}</p>
                        <p className="text-sm text-neutral-500">
                          Requested by {request.requested_by_name} on {new Date(request.created_at).toLocaleDateString()}
                        </p>
                      </div>
                      <span className="text-xs bg-amber-100 text-amber-700 px-2 py-1 rounded">Pending</span>
                    </div>
                    <div className="bg-white rounded p-3 mb-3">
                      <p className="text-sm text-neutral-600"><strong>Reason:</strong> {request.reason || 'No reason provided'}</p>
                    </div>
                    
                    {processingRequest === request.id ? (
                      <div className="space-y-2">
                        <Label htmlFor={`admin-reason-${request.id}`}>Your reason (required)</Label>
                        <Textarea
                          id={`admin-reason-${request.id}`}
                          placeholder="Provide a reason for your decision..."
                          value={adminReason}
                          onChange={(e) => setAdminReason(e.target.value)}
                          rows={2}
                        />
                        <div className="flex gap-2 mt-2">
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => {
                              setProcessingRequest(null);
                              setAdminReason('');
                            }}
                          >
                            Cancel
                          </Button>
                          <Button 
                            size="sm" 
                            variant="destructive"
                            onClick={() => handleRejectDeletionRequest(request.id)}
                            disabled={!adminReason.trim()}
                          >
                            <XCircle className="w-4 h-4 mr-1" /> Reject
                          </Button>
                          <Button 
                            size="sm"
                            onClick={() => handleApproveDeletionRequest(request.id)}
                            disabled={!adminReason.trim()}
                          >
                            <CheckCircle className="w-4 h-4 mr-1" /> Approve & Delete
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div className="flex gap-2">
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => setProcessingRequest(request.id)}
                        >
                          Review
                        </Button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
          <div className="flex justify-end pt-4 border-t">
            <Button variant="outline" onClick={() => setShowDeletionRequestsModal(false)}>
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Create Project Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Create New Project</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-neutral-500 py-4">
            Projects are automatically created when a Quote with installation is converted to a Sales Order.
            <br /><br />
            To create a project manually, go to <strong>Quotes</strong> → Create a quote with installation → Convert to Sales Order.
          </p>
          <div className="flex justify-end">
            <Button variant="outline" onClick={() => setShowCreateModal(false)}>Close</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ProjectsTab;
